import React, { useEffect, useState } from 'react';
import { Input } from '@cotera/client/v0/components/ui/input';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Text } from '@cotera/client/app/components/ui';
import { toast } from '@cotera/client/app/components/ui';
import { useQueryClient } from '@tanstack/react-query';
import { ResourceType } from '@cotera/api';
import { PersistedWorkflow } from '@cotera/sdk/core';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { SchedulePicker } from '@cotera/client/app/v1/trigger/schedule-picker';
import { humanReadableSchedule } from '@cotera/client/app/v1/trigger/schedule-utils';
import type { WorkflowCronTriggerViewData } from '../types';
import { useWorkflowGraphMutations } from '../../../use-workflow-graph-mutations';
import { useWorkflowModel } from '../../../workflow.model';
import { draftUpdateCronTrigger } from '../../../workflow-draft-graph';
import { WorkflowIdempotencyKeyEditor } from '../../workflow-idempotency-key-editor';

const { PersistedWorkflowTriggerKind } = PersistedWorkflow;

export const CronTriggerEditor: React.FC<{
  workflowId: string;
  bindingId: string;
  trigger: WorkflowCronTriggerViewData;
  onSaved?: () => void;
}> = ({ workflowId, bindingId, trigger, onSaved }) => {
  const model = useWorkflowModel();
  const mutations = useWorkflowGraphMutations();
  const client = useTenantedClient();
  const queryClient = useQueryClient();
  const orgId = useWhoami((s) => s.org.id);

  const [name, setName] = useState(trigger.name);
  const [nameEdited, setNameEdited] = useState(true);
  const [schedule, setSchedule] = useState(trigger.cron);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(trigger.name);
    setSchedule(trigger.cron);
    setNameEdited(true);
  }, [trigger.id, trigger.name, trigger.cron]);

  useEffect(() => {
    if (!nameEdited) {
      setName(humanReadableSchedule(schedule));
    }
  }, [schedule, nameEdited]);

  const isPersistedOnServer = model
    .getBaselineDraftState()
    .graph.triggers.some(
      (snapshot) =>
        snapshot.trigger.id === trigger.id &&
        snapshot.kind === PersistedWorkflowTriggerKind.Cron
    );

  const trimmedName = name.trim();
  const canSave = trimmedName !== '' && schedule.trim() !== '';

  const handleSave = async (): Promise<void> => {
    if (!canSave) {
      return;
    }
    setSaving(true);

    const patch = {
      bindingId,
      name: trimmedName,
      schedule,
      timezone: trigger.timezone,
    };

    if (!isPersistedOnServer) {
      const saved = await mutations.updateCronTrigger(patch);
      setSaving(false);
      if (!saved) {
        return;
      }
      toast.success('Schedule updated');
      onSaved?.();
      return;
    }

    const result = await client.resource.workflow.updateTrigger({
      workflowId,
      bindingId,
      triggerId: trigger.id,
      name: trimmedName,
      schedule,
      timezone: trigger.timezone,
    });
    if (result.isErr()) {
      setSaving(false);
      toast.error(
        result.error.t === 'application'
          ? result.error.err.message
          : 'Failed to update schedule'
      );
      return;
    }

    const idempotencyResult = await model.persistIdempotencyKey(
      model.getIdempotencyKey()
    );
    if (!idempotencyResult.success) {
      setSaving(false);
      toast.error(idempotencyResult.message);
      return;
    }

    await mutations.updateCronTrigger(patch);
    // Keep baseline in sync so an immediate API save doesn't look like an
    // unsaved graph edit (and doesn't require a full rehydrate that would
    // wipe other draft changes).
    model.updateBaselineDraftState((state) =>
      draftUpdateCronTrigger(state, patch)
    );
    void queryClient.invalidateQueries({
      queryKey: queryKeys.resource.get({
        orgId,
        type: ResourceType.Workflow,
        id: workflowId,
      }),
    });
    setSaving(false);
    toast.success('Schedule updated');
    onSaved?.();
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Text.Caption className="text-muted-foreground block">
          Name
        </Text.Caption>
        <Input
          value={name}
          onChange={(event) => {
            setNameEdited(true);
            setName(event.target.value);
          }}
          placeholder="Name this schedule"
        />
      </div>
      <div className="space-y-1.5">
        <Text.Caption className="text-muted-foreground block">
          Schedule
        </Text.Caption>
        <SchedulePicker value={schedule} onChange={setSchedule} />
      </div>
      <WorkflowIdempotencyKeyEditor
        value={model.getIdempotencyKey()}
        onChange={(next) => model.setIdempotencyKey(next)}
        disabled={saving}
      />
      <Button
        type="button"
        className="w-full"
        disabled={saving || !canSave}
        onClick={() => void handleSave()}
      >
        Save schedule
      </Button>
    </div>
  );
};
